import { formatPriceDisplay } from './cashPricing';
import {
    BUSINESS_NAME,
    RAHBER_LOGO_URL,
    BUSINESS_ADDRESS_FULL,
    BUSINESS_EMAIL,
    BUSINESS_PHONE_MAIN,
} from '../config/businessInfo';

const GENERIC_BRAND_PLACEHOLDERS = new Set([
    'rahber appliance',
    'rahber electronics',
    'rahber corporation',
    'genuine home appliance',
]);

const GENERIC_SKU_PLACEHOLDERS = new Set([
    'appl-01',
    'prod-1',
    'n/a',
]);

function toFiniteNumber(val, fallback = 0) {
    if (val === null || val === undefined || val === '') return fallback;
    const cleaned = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : val;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : fallback;
}

function normalizeDigits(phone) {
    return String(phone || '').replace(/\D/g, '');
}

function formatPhoneForDisplay(raw) {
    const str = String(raw || '').trim();
    if (!str) return '';
    // If customer entered 10-digit mobile without leading 0 or +92 (due to +92 input prefix in form)
    if (/^3\d{9}$/.test(str)) {
        return `0${str.slice(0, 3)}-${str.slice(3)}`;
    }
    if (/^03\d{9}$/.test(str)) {
        return `${str.slice(0, 4)}-${str.slice(4)}`;
    }
    return str;
}

function extractCleanCustomerNotes(rawNotes) {
    const text = String(rawNotes || '').trim();
    if (!text) return '';

    const additionalMatch = text.match(/Additional Notes:\s*(.+)$/i);
    if (additionalMatch && additionalMatch[1]) {
        return additionalMatch[1].trim();
    }

    const shortNotesMatch = text.match(/\.\s*Notes:\s*(.+)$/i);
    if (shortNotesMatch && shortNotesMatch[1]) {
        return shortNotesMatch[1].trim();
    }

    if (/^Delivery Preference:\s*[^.]+$/i.test(text)) {
        return '';
    }

    return text;
}

function parsePlanDetailsString(planDetails = '') {
    const str = String(planDetails || '');
    if (!str) return {};

    const monthsMatch = str.match(/(\d+)\s*(?:Mo|Month)/i);
    const dpPercentMatch = str.match(/(\d+(?:\.\d+)?)%\s*Down/i);
    const downAmtMatch = str.match(/(?:Down|Advance[^:]*):\s*(?:Rs\.?|PKR)\s*([\d,]+)/i);
    const monthlyMatch = str.match(/Monthly:\s*(?:Rs\.?|PKR)\s*([\d,]+)/i);
    const totalMatch = str.match(/Total:\s*(?:Rs\.?|PKR)\s*([\d,]+)/i);

    return {
        planMonths: monthsMatch ? parseInt(monthsMatch[1], 10) : null,
        downPaymentPercent: dpPercentMatch ? Number(dpPercentMatch[1]) : null,
        downPaymentAmount: downAmtMatch ? toFiniteNumber(downAmtMatch[1], null) : null,
        monthlyInstallment: monthlyMatch ? toFiniteNumber(monthlyMatch[1], null) : null,
        totalContractAmount: totalMatch ? toFiniteNumber(totalMatch[1], null) : null,
    };
}

function formatOrderDateTime(rawDate) {
    let dateObj = rawDate ? new Date(rawDate) : new Date();
    if (Number.isNaN(dateObj.getTime())) {
        dateObj = new Date();
    }

    const formattedDate = new Intl.DateTimeFormat('en-PK', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    }).format(dateObj);

    const formattedTime = new Intl.DateTimeFormat('en-PK', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(dateObj);

    return {
        formattedDate,
        formattedTime,
        fullDateTime: `${formattedDate} · ${formattedTime}`,
    };
}

function formatStatusLabel(rawStatus) {
    const clean = String(rawStatus || '').trim();
    if (!clean || clean.toLowerCase() === 'pending') {
        return 'Confirmed · Pending Dispatch';
    }
    return clean.charAt(0).toUpperCase() + clean.slice(1);
}

/**
 * Normalizes any order object across QuickBuyModal, DirectOrderModal, PlaceOrderModal,
 * SuccessModal, or localStorage into a complete, print-ready receipt view model.
 */
export function buildReceiptViewModel(order, fallbackContext = {}) {
    if (!order && !fallbackContext?.product) {
        return null;
    }

    const safeOrder = order || {};
    const rawOrderId = String(
        safeOrder.order_number ||
        safeOrder.orderNumber ||
        safeOrder.id ||
        safeOrder._id ||
        'ORD-PENDING'
    )
        .trim()
        .replace(/^#+/, '');

    const { formattedDate, formattedTime, fullDateTime } = formatOrderDateTime(
        safeOrder.createdAt || safeOrder.created_at || safeOrder.updatedAt
    );

    const orderStatus = formatStatusLabel(safeOrder.status);

    // Resolve raw items list
    const clientItems = Array.isArray(safeOrder._clientItems) ? safeOrder._clientItems : [];
    const orderItems = Array.isArray(safeOrder.items) && safeOrder.items.length > 0
        ? safeOrder.items
        : clientItems;

    const effectiveRawItems =
        orderItems.length > 0
            ? orderItems.map((it, idx) => ({
                  ...(clientItems[idx] || {}),
                  ...it,
                  mrp:
                      toFiniteNumber(it?.mrp, 0) > 0
                          ? toFiniteNumber(it.mrp, 0)
                          : toFiniteNumber(clientItems[idx]?.mrp ?? fallbackContext.mrp, 0),
                  brand: it?.brand || clientItems[idx]?.brand || fallbackContext.product?.brand || '',
                  sku:
                      it?.sku ||
                      it?.model ||
                      clientItems[idx]?.sku ||
                      fallbackContext.product?.sku ||
                      fallbackContext.product?.model ||
                      '',
                  payment_plan:
                      it?.payment_plan ||
                      clientItems[idx]?.payment_plan ||
                      fallbackContext.purchaseMode ||
                      '',
                  plan_details:
                      it?.plan_details ||
                      clientItems[idx]?.plan_details ||
                      '',
              }))
            : fallbackContext.product
            ? [
                  {
                      name: fallbackContext.product.name || 'Home Appliance',
                      brand: fallbackContext.product.brand || '',
                      sku: fallbackContext.product.sku || fallbackContext.product.model || '',
                      quantity: fallbackContext.quantity || 1,
                      mrp: toFiniteNumber(fallbackContext.mrp ?? fallbackContext.product.mrp, 0),
                      unitPrice: toFiniteNumber(
                          fallbackContext.unitPrice ?? fallbackContext.product.cash_price ?? fallbackContext.product.mrp,
                          0
                      ),
                      payment_plan: fallbackContext.purchaseMode || 'cash',
                  },
              ]
            : [];

    // Determine purchase mode ('cash' | 'four_month' | 'installment')
    const instDetails = safeOrder.installment_details || fallbackContext.installmentDetails || null;
    const firstItemPlan = String(effectiveRawItems[0]?.payment_plan || '').toLowerCase();
    const firstItemPlanText = String(effectiveRawItems[0]?.plan_details || '');
    const rawOrderType = String(safeOrder.order_type || '').toLowerCase();

    let purchaseMode = 'cash';
    if (
        instDetails?.mode === 'four_month' ||
        firstItemPlan === 'four_month' ||
        fallbackContext.purchaseMode === 'four_month' ||
        /4-month\s*0%/i.test(firstItemPlanText)
    ) {
        purchaseMode = 'four_month';
    } else if (
        instDetails?.mode === 'installment' ||
        firstItemPlan === 'installment' ||
        rawOrderType === 'installment' ||
        fallbackContext.purchaseMode === 'installment'
    ) {
        purchaseMode = 'installment';
    }

    const isInstallmentOrder = purchaseMode === 'four_month' || purchaseMode === 'installment';
    const orderTypeLabel =
        purchaseMode === 'four_month'
            ? '4-Month 0% Markup Plan'
            : purchaseMode === 'installment'
            ? 'Monthly Installment Plan'
            : 'Direct Cash Purchase';

    // Customer & Delivery fields
    const customerName = String(
        safeOrder.customer_name || safeOrder.customerName || 'Valued Customer'
    ).trim();

    const rawPhone = safeOrder.customer_phone || safeOrder.customerPhone || safeOrder.phone || '';
    const rawWhatsapp = safeOrder.customer_whatsapp || safeOrder.customerWhatsapp || '';
    const customerPhone = formatPhoneForDisplay(rawPhone);
    const hasDistinctWhatsapp =
        Boolean(normalizeDigits(rawWhatsapp)) &&
        normalizeDigits(rawWhatsapp) !== normalizeDigits(rawPhone);
    const customerWhatsapp = hasDistinctWhatsapp ? formatPhoneForDisplay(rawWhatsapp) : '';
    const customerEmail = String(safeOrder.customer_email || safeOrder.customerEmail || '').trim();

    const deliveryCity = String(safeOrder.delivery_city || safeOrder.deliveryCity || '').trim();
    const deliveryArea = String(safeOrder.delivery_area || safeOrder.lahore_area || '').trim();
    let deliveryAddress = String(
        safeOrder.delivery_address || safeOrder.deliveryAddress || safeOrder.address || ''
    ).trim();
    if (deliveryAddress && deliveryCity && !deliveryAddress.toLowerCase().includes(deliveryCity.toLowerCase())) {
        deliveryAddress = `${deliveryAddress}, ${deliveryCity}`;
    }

    const deliveryTime = String(safeOrder.delivery_time || '').trim();
    const paymentMethod = String(
        safeOrder.payment_method || safeOrder.paymentMethod || 'Advance Bank Transfer (Meezan / HBL)'
    ).trim();
    const customerNotes = extractCleanCustomerNotes(safeOrder.notes);

    // Build normalized line items
    const orderSubtotalField = toFiniteNumber(safeOrder.subtotal, 0);
    const orderDiscountField = toFiniteNumber(safeOrder.discount_amount, 0);
    const orderTotalField = toFiniteNumber(safeOrder.total_amount ?? safeOrder.totalAmount, 0);

    const items = effectiveRawItems.map((it, idx) => {
        const qty = Math.max(1, parseInt(it.quantity, 10) || 1);
        const rawBrand = typeof it.brand === 'object' ? it.brand?.name || '' : String(it.brand || '').trim();
        const cleanBrand = GENERIC_BRAND_PLACEHOLDERS.has(rawBrand.toLowerCase()) ? '' : rawBrand;

        const rawSku = String(it.sku || it.model || '').trim();
        const cleanSku = GENERIC_SKU_PLACEHOLDERS.has(rawSku.toLowerCase()) ? '' : rawSku;

        const itemName = String(it.name || 'Home Appliance').trim();
        const rawMrp = toFiniteNumber(it.mrp, 0);
        const rawUnit = toFiniteNumber(
            it.unitPrice ?? it.unit_price ?? it.price ?? it.cashPrice ?? it.cash_price,
            rawMrp
        );

        if (isInstallmentOrder) {
            const retailUnit = Math.round(rawMrp > 0 ? rawMrp : rawUnit);
            const lineTotal = retailUnit * qty;
            return {
                index: idx + 1,
                name: itemName,
                brand: cleanBrand,
                sku: cleanSku,
                quantity: qty,
                unitPrice: retailUnit,
                discountAmount: 0,
                discountPercent: 0,
                lineTotal,
                unitPriceFormatted: formatPriceDisplay(retailUnit),
                discountFormatted: '—',
                lineTotalFormatted: formatPriceDisplay(lineTotal),
            };
        }

        // Cash order item calculation
        const explicitLineTotal = toFiniteNumber(it.totalPrice ?? it.total_price, 0);
        const lineNetTotal = explicitLineTotal > 0 ? Math.round(explicitLineTotal) : Math.round(rawUnit * qty);
        const effectiveNetUnit = lineNetTotal / qty;

        let listUnitPrice = Math.round(effectiveNetUnit);
        let lineDiscount = 0;

        if (rawMrp > effectiveNetUnit + 0.5) {
            listUnitPrice = Math.round(rawMrp);
            lineDiscount = Math.max(0, listUnitPrice * qty - lineNetTotal);
        } else if (
            effectiveRawItems.length === 1 &&
            orderSubtotalField > lineNetTotal &&
            orderDiscountField > 0
        ) {
            listUnitPrice = Math.round(orderSubtotalField / qty);
            lineDiscount = Math.max(0, listUnitPrice * qty - lineNetTotal);
        }

        const discountPercent =
            lineDiscount > 0 && listUnitPrice > 0
                ? Math.round((lineDiscount / (listUnitPrice * qty)) * 100)
                : 0;

        return {
            index: idx + 1,
            name: itemName,
            brand: cleanBrand,
            sku: cleanSku,
            quantity: qty,
            unitPrice: listUnitPrice,
            discountAmount: lineDiscount,
            discountPercent,
            lineTotal: lineNetTotal,
            unitPriceFormatted: formatPriceDisplay(listUnitPrice),
            discountFormatted: lineDiscount > 0 ? `- ${formatPriceDisplay(lineDiscount)}` : '—',
            lineTotalFormatted: formatPriceDisplay(lineNetTotal),
        };
    });

    const totalQuantity = items.reduce((acc, item) => acc + item.quantity, 0);

    // Delivery charges resolution
    const rawShipping = safeOrder.delivery_charges ?? safeOrder.shipping_fee ?? safeOrder.deliveryFee;
    const deliveryChargesAmount =
        rawShipping !== undefined && rawShipping !== null && rawShipping !== ''
            ? Math.max(0, Math.round(toFiniteNumber(rawShipping, 0)))
            : 0;

    const isSelfPickup = paymentMethod.toLowerCase().includes('pickup');
    const isLahore =
        !deliveryCity ||
        deliveryCity.toLowerCase().includes('lahore') ||
        deliveryAddress.toLowerCase().includes('lahore');

    const deliveryChargesLabel =
        deliveryChargesAmount > 0
            ? formatPriceDisplay(deliveryChargesAmount)
            : isSelfPickup
            ? 'Free (Showroom Pickup)'
            : isLahore
            ? 'Free (Lahore Delivery)'
            : 'Standard Dispatch';

    // Financial summary resolution
    const itemsGrossSum = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
    const itemsDiscountSum = items.reduce((sum, item) => sum + item.discountAmount, 0);
    const itemsNetSum = items.reduce((sum, item) => sum + item.lineTotal, 0);

    let subtotal = itemsGrossSum > 0 ? itemsGrossSum : Math.round(orderSubtotalField || orderTotalField);
    let discountAmount = itemsDiscountSum;
    let markupAmount = 0;
    let markupRate = null;
    let totalOrderAmount = 0;
    let advanceAmount = 0;
    let advanceLabel = '';
    let remainingBalance = 0;
    let monthlyInstallment = 0;
    let installmentMonths = null;
    let downPaymentPercent = null;
    let remainingInstallmentCount = null;

    if (!isInstallmentOrder) {
        if (orderDiscountField > discountAmount && orderSubtotalField >= itemsNetSum + orderDiscountField) {
            subtotal = Math.round(orderSubtotalField);
            discountAmount = Math.round(orderDiscountField);
        }
        totalOrderAmount =
            orderTotalField > 0
                ? Math.round(orderTotalField)
                : Math.max(0, subtotal - discountAmount + deliveryChargesAmount);
    } else {
        const parsedFromText = parsePlanDetailsString(firstItemPlanText);
        if (purchaseMode === 'four_month') {
            installmentMonths = 4;
            downPaymentPercent = 25;
            markupRate = 0;
            markupAmount = 0;
            subtotal = Math.max(subtotal, Math.round(orderSubtotalField));
            totalOrderAmount = subtotal + deliveryChargesAmount;
            advanceAmount = Math.round(
                toFiniteNumber(
                    instDetails?.downPaymentAmount ?? orderTotalField,
                    Math.round(subtotal / 4)
                )
            );
            remainingBalance = Math.max(0, totalOrderAmount - advanceAmount);
            monthlyInstallment = Math.round(
                toFiniteNumber(
                    instDetails?.monthlyInstallment,
                    Math.round(remainingBalance / 3)
                )
            );
            remainingInstallmentCount = 3;
            advanceLabel = 'Advance Payment (1st Installment · 25%)';
        } else {
            installmentMonths =
                instDetails?.planMonths ||
                parsedFromText.planMonths ||
                fallbackContext.selectedPlan?.months ||
                null;
            downPaymentPercent =
                instDetails?.downPaymentPercent ??
                parsedFromText.downPaymentPercent ??
                fallbackContext.selectedPlan?.down_payment_percent ??
                null;
            markupRate =
                instDetails?.markupRate !== undefined ? instDetails.markupRate : null;

            subtotal = Math.max(subtotal, Math.round(orderSubtotalField));

            advanceAmount = Math.round(
                toFiniteNumber(
                    instDetails?.downPaymentAmount ??
                        parsedFromText.downPaymentAmount ??
                        orderTotalField,
                    0
                )
            );

            monthlyInstallment = Math.round(
                toFiniteNumber(
                    instDetails?.monthlyInstallment ?? parsedFromText.monthlyInstallment,
                    0
                )
            );

            const contractBase = Math.round(
                toFiniteNumber(
                    instDetails?.totalContractAmount ?? parsedFromText.totalContractAmount,
                    installmentMonths && monthlyInstallment > 0
                        ? advanceAmount + monthlyInstallment * installmentMonths
                        : subtotal
                )
            );

            const effectiveContract = Math.max(subtotal, contractBase);
            markupAmount = Math.max(0, effectiveContract - subtotal);
            totalOrderAmount = effectiveContract + deliveryChargesAmount;
            remainingBalance = Math.max(0, totalOrderAmount - advanceAmount);
            remainingInstallmentCount = installmentMonths;
            advanceLabel =
                downPaymentPercent !== null && downPaymentPercent !== undefined
                    ? `Down Payment / Advance (${downPaymentPercent}%)`
                    : 'Down Payment / Advance';
        }
    }

    const phoneNumbersList = Array.isArray(BUSINESS_PHONE_MAIN)
        ? BUSINESS_PHONE_MAIN.slice(0, 2).map((p) => p.display).join(' · ')
        : String(BUSINESS_PHONE_MAIN || '042-37145264');

    const primaryHelpline = Array.isArray(BUSINESS_PHONE_MAIN)
        ? BUSINESS_PHONE_MAIN[0]?.display || '042-37145264'
        : String(BUSINESS_PHONE_MAIN || '042-37145264');

    return {
        company: {
            name: BUSINESS_NAME,
            logoUrl: RAHBER_LOGO_URL,
            tagline: 'Authorized Electronics & Home Appliances Showroom (Est. 1980)',
            address: BUSINESS_ADDRESS_FULL,
            phones: phoneNumbersList,
            primaryHelpline,
            email: BUSINESS_EMAIL,
            website: 'www.rahbercorporation.com',
        },
        orderNumber: rawOrderId,
        orderNumberDisplay: `#${rawOrderId}`,
        formattedDate,
        formattedTime,
        fullDateTime,
        orderStatus,
        purchaseMode,
        isInstallmentOrder,
        orderTypeLabel,
        paymentMethod,
        customer: {
            name: customerName,
            phone: customerPhone,
            whatsapp: customerWhatsapp,
            email: customerEmail,
        },
        delivery: {
            address: deliveryAddress,
            city: deliveryCity,
            area: deliveryArea,
            schedule: deliveryTime,
            notes: customerNotes,
        },
        items,
        totalQuantity,
        summary: {
            subtotal,
            subtotalFormatted: formatPriceDisplay(subtotal),
            discountAmount,
            discountFormatted:
                discountAmount > 0 ? `- ${formatPriceDisplay(discountAmount)}` : formatPriceDisplay(0),
            markupAmount,
            markupRate,
            markupFormatted:
                markupAmount > 0 ? `+ ${formatPriceDisplay(markupAmount)}` : formatPriceDisplay(0),
            deliveryChargesAmount,
            deliveryChargesLabel,
            totalOrderAmount,
            totalOrderAmountFormatted: formatPriceDisplay(totalOrderAmount),
            advanceLabel,
            advanceAmount,
            advanceAmountFormatted: formatPriceDisplay(advanceAmount),
            remainingBalance,
            remainingBalanceFormatted: formatPriceDisplay(remainingBalance),
            monthlyInstallment,
            monthlyInstallmentFormatted:
                monthlyInstallment > 0 ? formatPriceDisplay(monthlyInstallment) : '',
            installmentMonths,
            downPaymentPercent,
            remainingInstallmentCount,
        },
    };
}
